import { useState } from "react";
import { toast } from "sonner";
import { FormPage, TextField, AreaField, SelectField, SubmitButton, SuccessPanel } from "@/components/forms";
import { api, apiError } from "@/lib/api";

const EMPTY = {
  your_name: "",
  organization: "",
  role: "",
  email: "",
  phone: "",
  referral_type: "",
  patient_summary: "",
  urgency: "",
};

export default function Referral() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/forms/referral", form);
      setSent(true);
      toast.success("Referral submitted — thank you.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormPage
      testid="referral-page"
      eyebrow="Submit a Referral"
      lines={["A clear handoff", "starts here."]}
      intro="For discharge planners, physicians, and care teams. A short summary is all we need — please don't include detailed medical records in this form."
      asideTitle="What happens next"
      asideText="Our team reviews every referral promptly and follows up with you and the family to coordinate the transition home."
    >
      {sent ? (
        <SuccessPanel
          testid="referral-success"
          title="Referral received"
          message="Thank you. Our team will review the referral and follow up to coordinate next steps."
          onReset={() => {
            setForm(EMPTY);
            setSent(false);
          }}
        />
      ) : (
        <form onSubmit={onSubmit} data-testid="referral-form" className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField id="rf-name" label="Your name" required value={form.your_name} onChange={set("your_name")} />
            <TextField id="rf-org" label="Organization / facility" required value={form.organization} onChange={set("organization")} />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField id="rf-role" label="Your role (optional)" value={form.role} onChange={set("role")} placeholder="e.g. Discharge planner" />
            <SelectField
              id="rf-type"
              label="Referral type"
              value={form.referral_type}
              onChange={set("referral_type")}
              options={["Hospital discharge", "Physician referral", "Facility referral", "Self / family referral", "Other"]}
            />
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <TextField id="rf-email" label="Work email" type="email" required value={form.email} onChange={set("email")} />
            <TextField id="rf-phone" label="Phone" type="tel" required value={form.phone} onChange={set("phone")} />
          </div>
          <AreaField
            id="rf-summary"
            label="Patient summary / reason for referral"
            required
            value={form.patient_summary}
            onChange={set("patient_summary")}
            placeholder="A brief, non-clinical summary is perfect — anticipated discharge timing, general support needs, and anything your team wants handed off."
          />
          <SelectField
            id="rf-urgency"
            label="Urgency"
            value={form.urgency}
            onChange={set("urgency")}
            options={["Routine", "Within the next week", "Urgent — discharge imminent"]}
          />
          <SubmitButton loading={loading} testid="referral-submit-btn">
            Submit Referral
          </SubmitButton>
        </form>
      )}
    </FormPage>
  );
}
